import { datadogLogs } from '@datadog/browser-logs';
import { useUserActivity, type ProductSwitcherItem } from '@deque/ui-navbar';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef
} from 'react';
import { useAuthContext } from './auth';
import { useEnterprises } from './enterprises';
import { useFeatureFlagState } from './featureFlags';

interface UserActivityState {
  sendUserActivity: () => void;
}
const UserActivityContext = createContext<UserActivityState>({
  sendUserActivity: () => {
    // Empty implementation for default context
  }
});

interface UserActivityProviderProps {
  children: React.ReactNode;
  productSlug: ProductSwitcherItem['slug'];
  sendUserActivity?: boolean;
}

const FOUR_HOURS_IN_MS = 4 * 60 * 60 * 1000;

export const UserActivityProvider: React.FC<UserActivityProviderProps> = ({
  children,
  productSlug,
  sendUserActivity = true
}) => {
  const { user } = useAuthContext();
  const { activeEnterprise } = useEnterprises();
  const activeEnterpriseId = activeEnterprise?.id;
  const isActivityTrackingEnabled = useFeatureFlagState('user_activity_v1');

  const authToken = user?.token || '';
  const isEnabled = sendUserActivity && isActivityTrackingEnabled;

  const { sendUserActivity: hookSendUserActivity } = useUserActivity({
    accountPortalUrl: window.location.origin,
    authToken,
    productSlug,
    activeEnterpriseId
  });

  const lastActivityTime = useRef<number>(0);
  const hasInitialActivityBeenSent = useRef<boolean>(false);

  const isReady = !!activeEnterpriseId && !!authToken;

  const safeSendUserActivity = useCallback(async () => {
    try {
      await hookSendUserActivity();
    } catch (error) {
      datadogLogs.logger.error('Failed to send user activity', {
        error,
        userId: user?.id,
        activeEnterpriseId,
        productSlug,
        timestamp: new Date().toISOString()
      });
    }
  }, [hookSendUserActivity, user?.id, activeEnterpriseId, productSlug]);

  const throttledSendActivity = useCallback(() => {
    if (!isReady) {
      return;
    }

    const now = Date.now();

    if (now - lastActivityTime.current >= FOUR_HOURS_IN_MS) {
      lastActivityTime.current = now;
      safeSendUserActivity();
    }
  }, [safeSendUserActivity, isReady]);

  useEffect(() => {
    if (!isEnabled || !isReady || hasInitialActivityBeenSent.current) {
      return;
    }

    safeSendUserActivity();
    lastActivityTime.current = Date.now();
    hasInitialActivityBeenSent.current = true;
  }, [isEnabled, isReady, safeSendUserActivity]);

  useEffect(() => {
    if (!isEnabled || !isReady) {
      return;
    }

    const handleUserActivity = () => {
      throttledSendActivity();
    };

    document.addEventListener('click', handleUserActivity, { capture: true });
    document.addEventListener('keydown', handleUserActivity, { capture: true });

    return () => {
      document.removeEventListener('click', handleUserActivity, {
        capture: true
      });
      document.removeEventListener('keydown', handleUserActivity, {
        capture: true
      });
    };
  }, [throttledSendActivity, isEnabled, activeEnterpriseId]);

  return (
    <UserActivityContext.Provider
      value={{ sendUserActivity: throttledSendActivity }}
    >
      {children}
    </UserActivityContext.Provider>
  );
};

export const useUserActivityContext = (): UserActivityState =>
  useContext(UserActivityContext);
